// ═══════════════════════════════════════════════════════════════════
//  useMinimapSearchProps — bundle Minimap props from LiveFilterContext.
// ═══════════════════════════════════════════════════════════════════
//
//  Convenience wrapper: caller passes the active workflow id and
//  receives ready-to-spread props for the Minimap's Phase G overlay.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { useLiveFilter, useLiveFilterMatches } from '@/contexts/LiveFilterContext';
import { useSearch } from '@/contexts/SearchContext';

export interface MinimapSearchProps {
  matchedBlockIds: ReadonlySet<string>;
  probeTrailBlockIds: string[];
  activeSearchQuery: string;
}

/**
 * Returns Minimap props derived from the global Search + LiveFilter
 * contexts. Pass them into <Minimap {...minimapSearchProps} />.
 */
export function useMinimapSearchProps(workflowId: string | null): MinimapSearchProps {
  const { hits } = useLiveFilter();
  const { query } = useSearch();
  const matched = useLiveFilterMatches(workflowId);

  return useMemo(() => {
    const trail: string[] = [];
    // Pull blockIds out of hit ids of shape `wf:<id>/sheet:<sid>/block:<bid>`.
    for (const hit of hits) {
      if (!workflowId) break;
      if (hit.sourceId !== workflowId) continue;
      const m = hit.id.match(/\/block:([^/]+)$/);
      if (m) trail.push(m[1]);
      if (trail.length >= 5) break;
    }
    // Map matched (entity) ids to block ids — matched is a set of entity ids
    // like wf:<id>/sheet:<sid>/block:<bid>. We extract the bid suffix.
    const blockIdMatches = new Set<string>();
    for (const id of matched) {
      const m = id.match(/\/block:([^/]+)$/);
      if (m) blockIdMatches.add(m[1]);
    }
    return {
      matchedBlockIds: blockIdMatches,
      probeTrailBlockIds: trail,
      activeSearchQuery: query,
    };
  }, [hits, matched, query, workflowId]);
}
